import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gamepad2, Trophy, Brain, Disc3, Type, Zap, Clock, CheckCircle2, XCircle,
  ArrowRight, Loader2, Sparkles, Crown, Medal, Flame, RotateCcw,
  Layers, TrendingUp, BookOpen, Award, Lock, Share2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import StreakShareModal from '../components/StreakShareModal';
import BashmentReflexGame from '../components/BashmentReflexGame';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Brain, Disc3, Type, Zap, Gamepad2
};

const GAME_LEADERBOARD_TABS = [
  { id: 'all', label: 'All Games' },
  { id: 'reggae_trivia', label: 'Trivia' },
  { id: 'slang_puzzle', label: 'Slang' },
  { id: 'bashment_reflex', label: 'Reflex' },
];

// ============================================
// REGGAE TRIVIA GAME
// ============================================
const TriviaGame = ({ onClose, userId }) => {
  const [phase, setPhase] = useState('intro'); // intro | playing | result | gameover
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [lastResult, setLastResult] = useState(null); // {correct, correct_answer_index, fact, points_earned, is_final}
  const [finalStats, setFinalStats] = useState(null);
  const [difficulty, setDifficulty] = useState(null); // null = mixed
  const [startError, setStartError] = useState(null);

  const startGame = async () => {
    setLoading(true);
    setStartError(null);
    try {
      const res = await axios.post(`${API}/api/games/trivia/start`, {
        num_questions: 10,
        difficulty,
      }, { withCredentials: true });
      setSession({ id: res.data.session_id });
      setQuestion(res.data.question);
      setTotalQuestions(res.data.total_questions);
      setCurrentIndex(0);
      setScore(0);
      setPhase('playing');
      setSelectedAnswer(null);
      setLastResult(null);
    } catch (e) {
      const status = e.response?.status;
      const msg = status === 429
        ? 'Whoa selector — too many requests. Take a breath and try again in a few seconds.'
        : e.response?.data?.detail || 'Failed to start game. Please try again.';
      setStartError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (answerIndex) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(answerIndex);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/games/trivia/answer`, {
        session_id: session.id,
        question_id: question.id,
        answer_index: answerIndex,
      }, { withCredentials: true });
      setLastResult(res.data);
      setScore(res.data.score);
      setPhase('result');
      if (res.data.is_final) {
        setFinalStats({
          correct: res.data.correct_count,
          total: res.data.total_questions,
          percent: res.data.percent,
          final_score: res.data.final_score,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit answer');
      setSelectedAnswer(null);
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (lastResult?.is_final) {
      setPhase('gameover');
      return;
    }
    setQuestion(lastResult.next_question);
    setCurrentIndex(lastResult.current_index);
    setSelectedAnswer(null);
    setLastResult(null);
    setPhase('playing');
  };

  const resetGame = () => {
    setPhase('intro');
    setSession(null);
    setQuestion(null);
    setLastResult(null);
    setSelectedAnswer(null);
    setFinalStats(null);
    setScore(0);
  };

  if (phase === 'intro') {
    return (
      <div className="bg-gradient-to-br from-rastyle-orange to-red-600 p-1 rounded-lg" data-testid="trivia-intro">
        <div className="bg-black rounded-lg p-8 sm:p-12 text-center">
          <Brain className="w-16 h-16 text-rastyle-orange mx-auto mb-4" />
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight mb-3">
            Reggae Trivia
          </h2>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            10 questions on reggae legends, dancehall culture, Kenya's scene, and sound system history.
            Test your knowledge — climb the leaderboard.
          </p>

          {/* Difficulty selector */}
          <div className="mb-6">
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Choose Difficulty</p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { id: null, label: 'Mixed', emoji: '🎯' },
                { id: 'easy', label: 'Easy', emoji: '🟢' },
                { id: 'medium', label: 'Medium', emoji: '🟡' },
                { id: 'hard', label: 'Hard', emoji: '🔴' },
              ].map(d => (
                <button
                  key={String(d.id)}
                  onClick={() => setDifficulty(d.id)}
                  className={`px-4 py-2 text-sm font-medium border transition-all ${
                    difficulty === d.id
                      ? 'bg-rastyle-orange text-black border-rastyle-orange'
                      : 'border-white/20 text-white hover:border-rastyle-orange/60'
                  }`}
                >
                  {d.emoji} {d.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={startGame}
            disabled={loading}
            className="px-8 py-3.5 bg-rastyle-orange text-black font-heading font-bold uppercase tracking-wide hover:bg-orange-400 transition-all flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
            data-testid="start-trivia-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
            {startError ? 'Try Again' : 'Start Game'}
          </button>

          {startError && (
            <div
              className="mt-5 mx-auto max-w-md text-sm text-red-300 bg-red-500/10 border border-red-500/30 px-4 py-3 rounded"
              data-testid="trivia-start-error"
            >
              {startError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'gameover' && finalStats) {
    const isPerfect = finalStats.correct === finalStats.total;
    const isStrong = finalStats.percent >= 70;
    return (
      <div className="bg-gradient-to-br from-yellow-500 via-rastyle-orange to-red-600 p-1 rounded-lg" data-testid="trivia-gameover">
        <div className="bg-black rounded-lg p-8 sm:p-12 text-center">
          <Trophy className={`w-20 h-20 mx-auto mb-4 ${isPerfect ? 'text-yellow-400' : isStrong ? 'text-rastyle-orange' : 'text-zinc-400'}`} />
          <h2 className="font-heading font-extrabold text-4xl uppercase tracking-tight mb-2">
            {isPerfect ? 'Legend!' : isStrong ? 'Strong Vibes!' : 'Keep Practicing'}
          </h2>
          <p className="text-zinc-400 mb-6">{isPerfect ? 'A perfect score — true selector knowledge.' : isStrong ? 'Solid reggae IQ — close to perfect.' : 'Try again to level up.'}</p>

          <PostGameStreakChip userId={userId} />

          <div className="grid grid-cols-3 gap-3 mb-6 max-w-md mx-auto">
            <div className="p-4 bg-white/5 border border-white/10">
              <p className="text-3xl font-bold text-rastyle-orange">{finalStats.final_score}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Score</p>
            </div>
            <div className="p-4 bg-white/5 border border-white/10">
              <p className="text-3xl font-bold text-green-400">{finalStats.correct}/{finalStats.total}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Correct</p>
            </div>
            <div className="p-4 bg-white/5 border border-white/10">
              <p className="text-3xl font-bold text-yellow-400">{finalStats.percent}%</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Accuracy</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={resetGame}
              className="px-6 py-3 bg-rastyle-orange text-black font-bold uppercase tracking-wide hover:bg-orange-400 transition-all flex items-center justify-center gap-2"
              data-testid="play-again-btn"
            >
              <RotateCcw className="w-4 h-4" /> Play Again
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white font-medium hover:border-rastyle-orange transition-all flex items-center justify-center gap-2"
            >
              <Trophy className="w-4 h-4" /> View Leaderboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Playing / Result phase
  if (!question) return null;
  const isResult = phase === 'result' && lastResult;

  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden" data-testid="trivia-playing">
      {/* Header */}
      <div className="px-5 py-4 bg-black border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-rastyle-orange" />
          <span className="font-mono text-xs uppercase tracking-wider text-zinc-400">
            Question {currentIndex + 1} / {totalQuestions}
          </span>
        </div>
        <div className="flex items-center gap-2 text-rastyle-orange font-bold">
          <Flame className="w-4 h-4" />
          <span data-testid="score-display">{score} pts</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-zinc-800">
        <div
          className="h-full bg-gradient-to-r from-rastyle-orange to-yellow-400 transition-all duration-500"
          style={{ width: `${((currentIndex + (isResult ? 1 : 0)) / totalQuestions) * 100}%` }}
        />
      </div>

      <div className="p-6 sm:p-8">
        {/* Question */}
        <div className="mb-2 flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 uppercase tracking-wider font-mono ${
            question.difficulty === 'easy' ? 'bg-green-500/15 text-green-400'
            : question.difficulty === 'medium' ? 'bg-yellow-500/15 text-yellow-400'
            : 'bg-red-500/15 text-red-400'
          }`}>
            {question.difficulty}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{question.category}</span>
        </div>
        <h3 className="font-heading text-xl sm:text-2xl font-bold mb-6 leading-snug" data-testid="trivia-question">
          {question.question}
        </h3>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((opt, idx) => {
            let stateClass = 'bg-white/5 border-white/10 hover:border-rastyle-orange/50 hover:bg-white/10';
            let icon = null;
            if (isResult) {
              if (idx === lastResult.correct_answer_index) {
                stateClass = 'bg-green-500/15 border-green-500 text-green-300';
                icon = <CheckCircle2 className="w-5 h-5 text-green-400" />;
              } else if (idx === selectedAnswer && !lastResult.correct) {
                stateClass = 'bg-red-500/15 border-red-500 text-red-300';
                icon = <XCircle className="w-5 h-5 text-red-400" />;
              } else {
                stateClass = 'bg-white/5 border-white/10 opacity-50';
              }
            } else if (selectedAnswer === idx) {
              stateClass = 'bg-rastyle-orange/20 border-rastyle-orange';
            }
            return (
              <button
                key={idx}
                onClick={() => submitAnswer(idx)}
                disabled={selectedAnswer !== null}
                className={`w-full text-left p-4 border-2 transition-all flex items-center gap-3 font-medium ${stateClass} disabled:cursor-default`}
                data-testid={`trivia-option-${idx}`}
              >
                <span className={`w-7 h-7 flex items-center justify-center font-mono text-sm border ${
                  isResult && idx === lastResult.correct_answer_index ? 'border-green-500 text-green-400'
                  : isResult && idx === selectedAnswer ? 'border-red-500 text-red-400'
                  : 'border-white/20 text-zinc-400'
                }`}>
                  {['A','B','C','D'][idx]}
                </span>
                <span className="flex-1">{opt}</span>
                {icon}
              </button>
            );
          })}
        </div>

        {/* Result panel */}
        {isResult && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 p-4 rounded-md ${lastResult.correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}
          >
            <div className="flex items-start gap-3">
              {lastResult.correct ? (
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="font-bold text-lg">
                  {lastResult.correct ? `Correct! +${lastResult.points_earned} pts` : 'Wrong!'}
                </p>
                {lastResult.fact && (
                  <p className="text-sm text-zinc-300 mt-1">💡 {lastResult.fact}</p>
                )}
              </div>
            </div>
            <button
              onClick={nextQuestion}
              className="mt-4 w-full sm:w-auto px-6 py-2.5 bg-rastyle-orange text-black font-bold uppercase tracking-wide text-sm hover:bg-orange-400 transition-all flex items-center justify-center gap-2 ml-auto"
              data-testid="next-question-btn"
            >
              {lastResult.is_final ? 'See Results' : 'Next Question'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ============================================


// ============================================
// DANCEHALL SLANG PUZZLE GAME
// ============================================
const LANG_LABEL = {
  patwa: { label: 'Patwa 🇯🇲', color: 'text-fomo-amber' },
  sheng: { label: 'Sheng 🇰🇪', color: 'text-rastyle-orange' },
  dancehall: { label: 'Dancehall', color: 'text-euphoria-pink' },
  reggae_ke: { label: 'Reggae KE 🇰🇪', color: 'text-mpesa-green' },
};

const SlangPuzzleGame = ({ onClose, userId }) => {
  const [phase, setPhase] = useState('intro');   // intro | playing | feedback | gameover
  const [session, setSession] = useState(null);
  const [puzzle, setPuzzle] = useState(null);
  const [totalPuzzles, setTotalPuzzles] = useState(10);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startError, setStartError] = useState(null);

  const startGame = async () => {
    setLoading(true);
    setStartError(null);
    try {
      const res = await axios.post(`${API}/api/games/slang/start`, { num_puzzles: 10 }, { withCredentials: true });
      setSession({ id: res.data.session_id });
      setPuzzle(res.data.puzzle);
      setTotalPuzzles(res.data.total_puzzles);
      setCurrentIndex(0);
      setScore(0);
      setCorrectCount(0);
      setPhase('playing');
      setSelectedAnswer(null);
      setLastResult(null);
    } catch (e) {
      const status = e.response?.status;
      const msg = status === 429
        ? 'Whoa selector — too many requests. Take a breath and try again.'
        : e.response?.data?.detail || 'Failed to start puzzle.';
      setStartError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (idx) => {
    if (selectedAnswer !== null || !puzzle) return;
    setSelectedAnswer(idx);
    try {
      const res = await axios.post(`${API}/api/games/slang/answer`, {
        session_id: session.id,
        puzzle_id: puzzle.id,
        answer_index: idx,
      });
      setLastResult(res.data);
      setScore(res.data.total_score);
      setCorrectCount(res.data.correct_count);
      setPhase('feedback');
      setTimeout(() => {
        if (res.data.is_complete) {
          setPhase('gameover');
        } else if (res.data.next_puzzle) {
          setPuzzle(res.data.next_puzzle);
          setCurrentIndex(res.data.current_index);
          setSelectedAnswer(null);
          setLastResult(null);
          setPhase('playing');
        }
      }, 2400);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit');
      setSelectedAnswer(null);
    }
  };

  // INTRO
  if (phase === 'intro') {
    return (
      <div className="bg-surface border border-white/5 rounded-2xl p-8 text-center" data-testid="slang-intro">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-fomo-amber/15 border border-fomo-amber/30 rounded-2xl mb-4">
          <Type className="w-7 h-7 text-fomo-amber" />
        </div>
        <h2 className="font-heading font-black text-3xl uppercase tracking-tighter leading-none mb-2">
          Slang Puzzle
        </h2>
        <p className="text-zinc-400 text-sm mb-2 max-w-md mx-auto">
          25 fill-in-the-blank puzzles. Patwa, Sheng, Dancehall slang, Kenya reggae heritage.
        </p>
        <p className="text-zinc-500 text-xs mb-6">10 random puzzles · 12 points per correct</p>

        <button
          onClick={startGame}
          disabled={loading}
          className="px-8 h-12 bg-fomo-amber text-black font-heading font-bold uppercase tracking-wide rounded-full hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
          data-testid="start-slang-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          {startError ? 'Try Again' : 'Start Puzzle'}
        </button>

        {startError && (
          <div className="mt-5 mx-auto max-w-md text-sm text-red-300 bg-red-500/10 border border-red-500/30 px-4 py-3 rounded-xl" data-testid="slang-start-error">
            {startError}
          </div>
        )}
      </div>
    );
  }

  // PLAYING / FEEDBACK
  if ((phase === 'playing' || phase === 'feedback') && puzzle) {
    const lang = LANG_LABEL[puzzle.lang] || { label: puzzle.lang, color: 'text-zinc-400' };
    const progressPct = ((currentIndex) / totalPuzzles) * 100;
    return (
      <div className="bg-surface border border-white/5 rounded-2xl p-6 sm:p-8" data-testid="slang-game">
        {/* progress + score */}
        <div className="flex items-center justify-between mb-2">
          <span className="overline">Puzzle {currentIndex + 1}/{totalPuzzles}</span>
          <span className="font-mono font-bold text-fomo-amber">{score} pts</span>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full mb-6 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-fomo-amber to-rastyle-orange"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct + (phase === 'feedback' ? 100 / totalPuzzles : 0)}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <span className={`overline ${lang.color} mb-2 block`}>{lang.label}</span>
        <h3 className="font-heading font-extrabold text-2xl sm:text-3xl uppercase tracking-tight leading-snug mb-6" data-testid="slang-prompt">
          {puzzle.prompt}
        </h3>

        <div className="space-y-2.5">
          {puzzle.options.map((opt, idx) => {
            const isSelected = selectedAnswer === idx;
            const isCorrect = lastResult && lastResult.correct_answer_index === idx;
            const showResult = phase === 'feedback';
            let extra = 'border-white/10 hover:border-fomo-amber/40';
            if (showResult && isCorrect) extra = 'border-mpesa-green bg-mpesa-green/10 text-mpesa-green';
            else if (showResult && isSelected && !isCorrect) extra = 'border-euphoria-pink bg-euphoria-pink/10 text-euphoria-pink';
            else if (isSelected) extra = 'border-fomo-amber';

            return (
              <button
                key={idx}
                onClick={() => submitAnswer(idx)}
                disabled={selectedAnswer !== null}
                className={`w-full text-left h-14 px-5 rounded-xl border transition-all flex items-center justify-between gap-3 disabled:cursor-default ${extra}`}
                data-testid={`slang-option-${idx}`}
              >
                <span className="font-medium text-base">{opt}</span>
                {showResult && isCorrect && <CheckCircle2 className="w-5 h-5 text-mpesa-green shrink-0" />}
                {showResult && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-euphoria-pink shrink-0" />}
              </button>
            );
          })}
        </div>

        {phase === 'feedback' && lastResult && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 p-4 bg-white/[0.03] border border-white/10 rounded-xl"
            data-testid="slang-fact"
          >
            {lastResult.translation && (
              <p className="text-sm text-zinc-300"><span className="text-fomo-amber font-medium">Translation:</span> {lastResult.translation}</p>
            )}
            {lastResult.fact && (
              <p className="text-xs text-zinc-500 mt-1.5">{lastResult.fact}</p>
            )}
          </motion.div>
        )}
      </div>
    );
  }

  // GAMEOVER
  if (phase === 'gameover') {
    const pct = totalPuzzles ? Math.round((correctCount / totalPuzzles) * 100) : 0;
    return (
      <div className="bg-surface border border-fomo-amber/30 rounded-2xl p-8 text-center" data-testid="slang-gameover">
        <Trophy className="w-14 h-14 text-fomo-amber mx-auto mb-4" />
        <h2 className="font-heading font-black text-3xl uppercase tracking-tighter leading-none mb-1">Slang Master?</h2>
        <p className="text-zinc-500 text-sm mb-6">Here's your score for this round</p>

        <PostGameStreakChip userId={userId} />

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white/5 rounded-xl p-4">
            <p className="font-mono font-bold text-3xl text-fomo-amber">{score}</p>
            <p className="overline mt-1">Points</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <p className="font-mono font-bold text-3xl text-rastyle-orange">{correctCount}<span className="text-zinc-500 text-xl">/{totalPuzzles}</span></p>
            <p className="overline mt-1">Correct</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <p className="font-mono font-bold text-3xl text-euphoria-pink">{pct}%</p>
            <p className="overline mt-1">Accuracy</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={startGame}
            className="flex-1 h-12 bg-fomo-amber text-black font-bold uppercase tracking-wide rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="slang-play-again"
          >
            <RotateCcw className="w-4 h-4" /> Play Again
          </button>
          <button
            onClick={onClose}
            className="flex-1 h-12 border border-white/15 text-zinc-400 uppercase tracking-wide text-sm rounded-full hover:text-white"
            data-testid="slang-back-to-room"
          >
            Back to Room
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// LEADERBOARD
// ============================================
export const GamingLeaderboard = ({ refreshKey = 0 }) => {
  const [entries, setEntries] = useState([]);
  const [window, setWindow] = useState('weekly');
  const [game, setGame] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/games/leaderboard`, { params: { game, window, limit: 25 } })
      .then(res => setEntries(res.data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [game, window, refreshKey]);

  const rankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-zinc-500 font-mono text-sm">#{rank}</span>;
  };

  return (
    <div id="leaderboard" className="scroll-mt-24 bg-surface border border-white/5 rounded-2xl" data-testid="leaderboard">
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-fomo-amber" />
          <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight">Top Selectors</h3>
        </div>
        <div className="flex bg-black/40 rounded-full p-1">
          <button
            onClick={() => setWindow('weekly')}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide rounded-full transition-all ${window === 'weekly' ? 'bg-rastyle-orange text-black' : 'text-zinc-400 hover:text-white'}`}
          >
            This Week
          </button>
          <button
            onClick={() => setWindow('all_time')}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide rounded-full transition-all ${window === 'all_time' ? 'bg-rastyle-orange text-black' : 'text-zinc-400 hover:text-white'}`}
          >
            All Time
          </button>
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-white/5 px-3 py-2 scrollbar-hide" aria-label="Choose game leaderboard">
        {GAME_LEADERBOARD_TABS.map((item) => (
          <button
            type="button"
            key={item.id}
            onClick={() => setGame(item.id)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide ${game === item.id ? 'bg-fomo-amber text-black' : 'text-zinc-500 hover:text-white'}`}
            aria-pressed={game === item.id}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="p-3 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-rastyle-orange mx-auto" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 text-sm">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Be the first to claim the top spot!</p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {entries.map((e, idx) => {
              const rank = idx + 1;
              const topThree = rank <= 3;
              return (
                <li
                  key={e.user_id || idx}
                  className={`flex items-center gap-3 p-3 rounded-xl ${topThree
                    ? 'bg-gradient-to-r from-fomo-amber/10 to-transparent border-l-2 border-fomo-amber'
                    : 'bg-white/[0.03]'}`}
                  data-testid={`leaderboard-row-${idx}`}
                >
                  <div className="w-8 flex items-center justify-center font-mono">{rankIcon(rank)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{e.user_name}</p>
                    <p className="overline">
                      {e.plays} {e.plays === 1 ? 'play' : 'plays'}
                      {game === 'all' ? ` • ${e.games_played} games` : ` • ${e.best_correct || 0} correct`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-rastyle-orange text-lg leading-none">{game === 'all' ? e.championship_points : e.best_score}</p>
                    <p className="overline mt-0.5">{game === 'all' ? 'champ pts' : 'score'}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ============================================
// SELECTOR PROFILE (Phase 3 Rewards / Streak system)
// Shows the logged-in user's current/longest streak, badges earned,
// per-game best scores, and weekly Top-10 status.
// Anonymous users see a soft sign-up nudge instead.
// ============================================
const BADGE_ICON_MAP = {
  Sparkles, Layers, Flame, TrendingUp, Crown, Type, BookOpen, Trophy, Medal, Award,
};

const SelectorProfile = ({ userId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/api/games/profile`, { params: { user_id: userId } })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, refreshKey]);

  if (loading) {
    return (
      <div className="bg-surface border border-white/5 rounded-2xl p-6 mb-6 flex items-center justify-center" data-testid="selector-profile-loading">
        <Loader2 className="w-5 h-5 animate-spin text-rastyle-orange" />
      </div>
    );
  }

  // Anonymous / signed-out users — nudge them to sign up
  if (!userId || data?.anonymous) {
    return (
      <div
        className="bg-gradient-to-r from-rastyle-orange/10 via-fomo-amber/5 to-transparent border border-rastyle-orange/30 rounded-2xl p-5 sm:p-6 mb-6"
        data-testid="selector-profile-anon"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-rastyle-orange/20 border border-rastyle-orange/40 flex items-center justify-center flex-shrink-0">
            <Flame className="w-6 h-6 text-rastyle-orange" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-extrabold text-lg sm:text-xl uppercase tracking-tight mb-1">
              Sign up to build your streak
            </h3>
            <p className="text-zinc-400 text-sm">
              Earn selector badges, climb the weekly Top 10, and rack up daily streaks.
              Play anonymously now — but real progress needs a Rastyle account.
            </p>
          </div>
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-rastyle-orange text-black font-bold uppercase tracking-wide text-sm rounded-full hover:bg-orange-400 transition-colors flex-shrink-0"
            data-testid="selector-profile-signup-link"
          >
            Sign In <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const earnedIds = new Set((data.badges || []).map(b => b.id));
  const allBadges = data.all_badges || data.badges || [];
  const bestScores = data.best_scores || {};
  const gameLabels = { reggae_trivia: 'Reggae Trivia', slang_puzzle: 'Slang Puzzle' };

  return (
    <div
      className="bg-surface border border-white/5 rounded-2xl overflow-hidden mb-6"
      data-testid="selector-profile"
    >
      {/* Header strip */}
      <div className="px-5 sm:px-6 py-4 border-b border-white/5 bg-gradient-to-r from-rastyle-orange/10 to-transparent flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-rastyle-orange" />
          <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight">Your Selector Profile</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data.in_weekly_top_10 && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-fomo-amber/15 border border-fomo-amber/40 text-fomo-amber font-mono text-[11px] uppercase tracking-wider rounded-full"
              data-testid="selector-profile-top10-pill"
            >
              <Trophy className="w-3.5 h-3.5" /> Top 10 this week
            </span>
          )}
          {data.current_streak > 0 && (
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rastyle-orange text-black font-mono text-[11px] font-bold uppercase tracking-wider rounded-full hover:bg-orange-400 transition-colors"
              data-testid="selector-profile-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> Share Streak
            </button>
          )}
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-6">
        {/* Stat strip — streak, longest, plays */}
        <div className="grid grid-cols-3 gap-3" data-testid="selector-profile-stats">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Flame className={`w-5 h-5 ${data.current_streak > 0 ? 'text-rastyle-orange' : 'text-zinc-600'}`} />
              <p className="font-mono font-extrabold text-2xl sm:text-3xl leading-none">{data.current_streak}</p>
            </div>
            <p className="overline mt-1">Day Streak</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
            <p className="font-mono font-extrabold text-2xl sm:text-3xl text-fomo-amber leading-none">{data.longest_streak}</p>
            <p className="overline mt-1">Best Streak</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
            <p className="font-mono font-extrabold text-2xl sm:text-3xl text-rastyle-orange leading-none">{data.plays}</p>
            <p className="overline mt-1">Total Plays</p>
          </div>
        </div>

        {/* Per-game best scores */}
        {Object.keys(bestScores).length > 0 && (
          <div data-testid="selector-profile-bests">
            <p className="overline mb-2">Best Scores</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(bestScores).map(([game, b]) => (
                <div
                  key={game}
                  className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-xl px-4 py-3"
                  data-testid={`selector-profile-best-${game}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{gameLabels[game] || game}</p>
                    <p className="overline">{b.best_correct}/{b.total_questions} correct</p>
                  </div>
                  <p className="font-mono font-bold text-rastyle-orange text-lg">{b.best_score}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Badge wall */}
        {allBadges.length > 0 && (
          <div data-testid="selector-profile-badges">
            <div className="flex items-baseline justify-between mb-2">
              <p className="overline">Badges</p>
              <p className="overline text-zinc-500">
                {earnedIds.size}/{allBadges.length} unlocked
              </p>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-9 gap-2">
              {allBadges.map((badge) => {
                const earned = earnedIds.has(badge.id);
                const Icon = BADGE_ICON_MAP[badge.icon] || Trophy;
                return (
                  <div
                    key={badge.id}
                    title={`${badge.name} — ${badge.tagline}`}
                    className={`relative aspect-square rounded-xl p-1 flex items-center justify-center transition-all ${
                      earned
                        ? `bg-gradient-to-br ${badge.color}`
                        : 'bg-white/[0.02] border border-white/5'
                    }`}
                    data-testid={`badge-${badge.id}${earned ? '-earned' : '-locked'}`}
                  >
                    <div className={`w-full h-full rounded-lg flex items-center justify-center ${
                      earned ? 'bg-black/30 backdrop-blur-sm' : ''
                    }`}>
                      {earned ? (
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white drop-shadow" />
                      ) : (
                        <Lock className="w-4 h-4 text-zinc-600" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Share modal */}
      <StreakShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        userName={data.user_name || 'Selector'}
        currentStreak={data.current_streak || 0}
        longestStreak={data.longest_streak || 0}
        plays={data.plays || 0}
        badgeCount={earnedIds.size}
        totalBadges={allBadges.length}
      />
    </div>
  );
};

// Compact streak chip shown on the gameover screens to surface the new streak
// right after the user finishes a round. Refetches profile so the latest play
// is included.
const PostGameStreakChip = ({ userId }) => {
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    if (!userId || userId.startsWith('anon-')) return;
    axios.get(`${API}/api/games/profile`, { params: { user_id: userId } })
      .then(res => setStreak(res.data?.current_streak ?? 0))
      .catch(() => {});
  }, [userId]);

  if (streak === null || streak <= 0) return null;
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rastyle-orange/15 border border-rastyle-orange/40 text-rastyle-orange font-mono text-xs uppercase tracking-wider mb-4"
      data-testid="post-game-streak-chip"
    >
      <Flame className="w-4 h-4" />
      You're on a {streak}-day streak — keep it going!
    </div>
  );
};

// ============================================
// MAIN GAMING ROOM PAGE
// ============================================
const GamingRoomPage = () => {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [activeGame, setActiveGame] = useState(null); // 'reggae_trivia' or null
  const [profileRefresh, setProfileRefresh] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/games`).then(res => setGames(res.data || [])).catch(() => {});
  }, []);

  // Whenever the user exits a game session, bump the profile refresh key so the
  // SelectorProfile picks up any newly earned badges / streak days.
  const closeActiveGame = () => {
    setActiveGame(null);
    setProfileRefresh((k) => k + 1);
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <SEO
        title="Gaming Room — Rastyle Republik"
        description="Play reggae trivia, dancehall slang and sound-system challenges. Build streaks and compete across the Rastyle Republik gaming leaderboards."
        url="/gaming"
      />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-rastyle-orange/10 via-black to-red-900/10" />
        <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-rastyle-orange/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-rastyle-orange/15 border border-rastyle-orange/40 text-rastyle-orange font-mono text-[11px] uppercase tracking-wider">
              <Gamepad2 className="w-3.5 h-3.5" /> Rastyle Club OS™
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Gaming Room</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-3">
            Play. Score.
            <span className="text-rastyle-orange"> Shell di place.</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Reggae trivia, dancehall slang puzzles & sound-system challenges.
            Climb the leaderboard. Earn badges. Bragging rights, guaranteed.
          </p>
          <a
            href="#leaderboard"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-fomo-amber/40 bg-fomo-amber/10 px-5 py-2.5 text-sm font-bold text-fomo-amber hover:bg-fomo-amber hover:text-black"
          >
            <Trophy className="h-4 w-4" /> View live leaderboard
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 mt-10">
        {/* Active game OR game catalog */}
        {activeGame === 'reggae_trivia' || activeGame === 'slang_puzzle' || activeGame === 'reflex_tap' ? (
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              <button
                onClick={closeActiveGame}
                className="mb-4 text-sm text-zinc-400 hover:text-rastyle-orange flex items-center gap-1.5"
              >
                ← Back to Gaming Room
              </button>
              {activeGame === 'reggae_trivia' && (
                <TriviaGame onClose={closeActiveGame} userId={user?.user_id} />
              )}
              {activeGame === 'slang_puzzle' && (
                <SlangPuzzleGame onClose={closeActiveGame} userId={user?.user_id} />
              )}
              {activeGame === 'reflex_tap' && (
                <BashmentReflexGame onClose={closeActiveGame} userId={user?.user_id} />
              )}
            </div>
            <div>
              <GamingLeaderboard refreshKey={profileRefresh} />
            </div>
          </div>
        ) : (
          <>
            {/* Selector Profile — streak, badges, best scores */}
            <SelectorProfile userId={user?.user_id} refreshKey={profileRefresh} />

            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Games grid */}
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-rastyle-orange" /> Choose Your Game
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {games.map((g) => {
                  const Icon = ICON_MAP[g.icon] || Gamepad2;
                  const isLive = g.status === 'live';
                  return (
                    <button
                      key={g.id}
                      onClick={() => isLive && setActiveGame(g.id)}
                      disabled={!isLive}
                      className={`text-left p-5 rounded-lg border transition-all group relative overflow-hidden ${
                        isLive
                          ? 'bg-zinc-900 border-white/10 hover:border-rastyle-orange/60 hover:-translate-y-1 cursor-pointer'
                          : 'bg-zinc-900/50 border-white/5 cursor-not-allowed opacity-60'
                      }`}
                      data-testid={`game-card-${g.id}`}
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${g.color} opacity-20 rounded-full blur-2xl`} />
                      <div className="relative">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${g.color} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          {isLive ? (
                            <span className="text-[10px] px-2 py-0.5 bg-green-500/15 text-green-400 font-mono uppercase tracking-wider border border-green-500/30">
                              ● Live
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 bg-zinc-700/40 text-zinc-400 font-mono uppercase tracking-wider">
                              Soon
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading font-bold text-xl mb-1 group-hover:text-rastyle-orange transition-colors">
                          {g.name}
                        </h3>
                        <p className="text-sm text-zinc-400 mb-3">{g.tagline}</p>
                        <p className="text-xs text-zinc-500 line-clamp-2">{g.description}</p>
                        {isLive && (
                          <div className="mt-4 flex items-center gap-1.5 text-rastyle-orange text-sm font-medium group-hover:gap-2 transition-all">
                            Play now <ArrowRight className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Stats / Info card */}
              <div className="mt-6 p-5 rounded-lg bg-gradient-to-r from-rastyle-orange/10 to-red-900/10 border border-rastyle-orange/30">
                <div className="flex items-start gap-4">
                  <Trophy className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-lg mb-1">Weekly Champion gets bragging rights</h3>
                    <p className="text-sm text-zinc-400">
                      Score reset every Monday at 00:00 EAT. Highest weekly score wins the Rastyle Champion badge.
                      Coming soon: real-world rewards from Kenyan venues — free drinks, VIP upgrades, merch discounts.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="order-first lg:order-last">
              <GamingLeaderboard refreshKey={profileRefresh} />
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GamingRoomPage;
